import { Divider, Group, Select } from '@byecode/ui'
import type { Field, IndependentAction } from '@lighthouse/core'
import type { DownloadFileActionConfig, FlowNode, NodeTypes, VariableTree2 } from '@lighthouse/shared'
import {
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    InnerTypeMapByFieldType,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { getPexelsList } from '@/services'

import { GroupRequired } from '../../Common/GroupRequired'
import * as SC from '../../styles'

interface DownloadFileActionConfigureProps {
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    nodeType?: NodeTypes
    value: DownloadFileActionConfig
    onChange: (value: DownloadFileActionConfig) => void
}

const FIELD: Field = {
    id: '',
    dsId: '',
    type: 'file',
    name: 'fileUrl',
    innerType: InnerTypeMapByFieldType['file']
}

export const DownloadFileActionConfigure: React.FC<DownloadFileActionConfigureProps> = ({
    allParentNodes,
    allParentActions,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)
    const disabledWithVersion = useIsDisabledWithVersion()

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const { customViewOption } = useVariableCustomViewOption()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({ dataSource: viewDataSource, viewType })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: allParentNodes,
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="下载内容" required>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>选择附件</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <VariableSelect
                            disabled={disabledWithVersion}
                            field={FIELD}
                            innerTypes={['TEXT', 'ARRAY']}
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? pageOptions : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            imageOption={imageOption}
                            value={value.fileUrl}
                            onChange={fileUrl => onChange({ ...value, fileUrl })}
                            onFetchPexelsList={getPexelsList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </GroupRequired>
        </SC.Container>
    )
}
